import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Award, CheckCircle, AlertTriangle, Building2, FileText, Shield, RefreshCw } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = (): Record<string, string> => {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  };
};

interface Company { id: number; name: string; industry?: string; }
interface FrameworkAssignment {
  id: number;
  framework_id: number;
  framework_version_id?: number;
  framework_name: string;
  company_id: number;
  status: string;
  completion_percentage?: number;
}

const IssueCertification: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [assignments, setAssignments] = useState<FrameworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form
  const [selectedCompany, setSelectedCompany] = useState<number>(0);
  const [selectedAssignment, setSelectedAssignment] = useState<number>(0);
  const [certType, setCertType] = useState<string>('initial');
  const [auditReportUrl, setAuditReportUrl] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => { loadCompanies(); }, []);

  useEffect(() => {
    if (selectedCompany) loadAssignments(selectedCompany);
    else setAssignments([]);
    setSelectedAssignment(0);
  }, [selectedCompany]);

  async function loadCompanies() {
    setLoading(true);
    try {
      const res = await fetch('/api/companies?limit=500&sortBy=createdAt&sortOrder=desc', { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setCompanies(data.data || data.companies || []);
    } catch {
      // Fallback: try partner companies
      try {
        const res = await fetch('/api/companies?limit=500&sortBy=createdAt&sortOrder=desc', { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setCompanies(data.companies || data.data || []);
        }
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  }

  async function loadAssignments(companyId: number) {
    try {
      let res = await fetch(`/api/compliance/definition/framework-assignments/company/${companyId}`, { headers: authHeaders() });
      if (!res.ok) {
        // Backward-compatible fallback for older deployments
        res = await fetch(`/api/compliance/definition/assignments?company_id=${companyId}`, { headers: authHeaders() });
      }
      if (!res.ok) return;
      const data = await res.json();
      const items = data.data || data.assignments || [];
      const normalized: FrameworkAssignment[] = (items as any[]).map((a: any) => ({
        id: Number(a.id),
        framework_id: Number(a.framework_id ?? a.frameworkId),
        framework_version_id: Number(a.framework_version_id ?? a.frameworkVersionId) || undefined,
        framework_name: a.framework_name || a.frameworkName || `Framework #${a.framework_id ?? a.frameworkId}`,
        company_id: Number(a.company_id ?? a.companyId ?? companyId),
        status: a.status || 'active',
        completion_percentage: Number(
          a.completion_percentage ??
          a.completionPercentage ??
          (a.status === 'completed' ? 100 : 0)
        ),
      }));
      setAssignments(normalized.filter((a: FrameworkAssignment) => a.status === 'active' || a.status === 'completed'));
    } catch { setAssignments([]); }
  }

  const selectedAssignmentData = assignments.find(a => a.id === selectedAssignment);
  const readiness = selectedAssignmentData
    ? Number(selectedAssignmentData.completion_percentage ?? (selectedAssignmentData.status === 'completed' ? 100 : 0))
    : 0;
  const isReady = readiness >= 100 || selectedAssignmentData?.status === 'completed';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCompany || !selectedAssignment) {
      setError('Please select a company and framework assignment.');
      return;
    }
    if (!isReady) {
      setError(`Compliance score is ${readiness}%. 100% compliance is required for certification.`);
      return;
    }
    if (!overrideReason.trim() || overrideReason.trim().length < 20) {
      setError('Override reason is required and must be at least 20 characters. This is a system-admin escape hatch — explain why the normal per-control review flow was bypassed.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const assignment = assignments.find(a => a.id === selectedAssignment)!;
      const frameworkId = assignment.framework_id;
      const frameworkVersionId = assignment.framework_version_id;
      if (!frameworkId || !frameworkVersionId) {
        throw new Error('Selected framework assignment is missing framework or version details.');
      }
      const res = await fetch('/api/compliance/certifications/issue', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          companyId: selectedCompany,
          frameworkId,
          frameworkVersionId,
          auditorId: user?.id,
          certificationType: certType,
          auditReportUrl: auditReportUrl || undefined,
          adminOverrideReason: overrideReason.trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to issue certification');
      }
      setSuccess('Certification issued successfully (admin override recorded).');
      setSelectedCompany(0);
      setSelectedAssignment(0);
      setCertType('initial');
      setAuditReportUrl('');
      setOverrideReason('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Manual Issue Certificate <span className="text-sm font-medium text-amber-700 ml-2">— admin override</span></h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-0.5">Reserved for exceptional cases. The normal certification path is the External Auditor Portal.</p>
        </div>

        {/* Override warning banner */}
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 flex items-start gap-3">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
          <div className="text-sm text-amber-900 leading-relaxed">
            <p className="font-bold mb-1">This bypasses the per-control review workflow.</p>
            <p className="mb-2">
              Normally, an external auditor reviews each compliance page individually in the
              <a className="underline mx-1" href="/audit/dashboard">External Auditor Portal</a>
              and clicks <em>Issue Certificate</em> only after every control is marked Satisfied. That keeps the certificate snapshot defensible against ISO/IEC 17021-1.
            </p>
            <p className="font-medium">Use this page only for: paper-audit backfill, regulator-mandated retroactive issuance, mid-migration data, or auditor handover. Every issuance from here is permanently logged as an admin override.</p>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="text-green-600 shrink-0" size={18} />
            <p className="text-green-800 text-sm font-medium">{success}</p>
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-600 hover:text-green-800 text-sm font-medium">Dismiss</button>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="text-red-600 shrink-0" size={18} />
            <p className="text-red-800 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium">Dismiss</button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="p-6 space-y-5">

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Building2 size={15} className="text-blue-600" /> Company
              </label>
              <select
                value={selectedCompany}
                onChange={e => setSelectedCompany(Number(e.target.value))}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
              >
                <option value={0}>Select a company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Framework Assignment */}
            {selectedCompany > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                  <FileText size={15} className="text-blue-600" /> Framework Assignment
                </label>
                {assignments.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No active framework assignments for this company.</p>
                ) : (
                  <select
                    value={selectedAssignment}
                    onChange={e => setSelectedAssignment(Number(e.target.value))}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                  >
                    <option value={0}>Select a framework...</option>
                    {assignments.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.framework_name || `Framework #${a.framework_id}`} — {Number(a.completion_percentage ?? 0).toFixed(0)}% complete
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Readiness indicator */}
            {selectedAssignment > 0 && (
              <div className={`rounded-lg p-4 flex items-center gap-3 ${isReady ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <Shield size={18} className={isReady ? 'text-green-600' : 'text-amber-600'} />
                <div>
                  <p className={`text-sm font-medium ${isReady ? 'text-green-800' : 'text-amber-800'}`}>
                    {isReady ? 'Ready for Certification' : 'Not Ready'}
                  </p>
                  <p className={`text-xs ${isReady ? 'text-green-600' : 'text-amber-600'}`}>
                    Compliance score: {readiness.toFixed(1)}% {!isReady && '(100% required — all pages must be certified)'}
                  </p>
                </div>
                <div className="ml-auto">
                  <div className="w-16 bg-slate-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${isReady ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(100, readiness)}%` }} />
                  </div>
                </div>
              </div>
            )}

            {/* Certification Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Certification Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { value: 'initial', label: 'Initial' },
                  { value: 'renewal', label: 'Renewal' },
                  { value: 'surveillance', label: 'Surveillance' },
                  { value: 'recertification', label: 'Recertification' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCertType(opt.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      certType === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Audit Report URL */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Audit Report URL (optional)</label>
              <input
                type="url"
                value={auditReportUrl}
                onChange={e => setAuditReportUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Override reason — required */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Reason for override <span className="text-red-600">*</span>
                <span className="text-slate-400 font-normal ml-1">(min 20 characters; recorded permanently)</span>
              </label>
              <textarea
                value={overrideReason}
                onChange={e => setOverrideReason(e.target.value)}
                rows={3}
                placeholder="e.g., Paper audit completed by BSI 2026-04-01, hash of signed report attached. Backfilling for regulator request."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                This reason is logged with the certificate and visible in the audit trail. It must justify why the External Auditor Portal flow was bypassed.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <p className="text-xs text-slate-400">Issuing as: {user?.first_name} {user?.last_name}</p>
            <button
              type="submit"
              disabled={submitting || !selectedCompany || !selectedAssignment || !isReady}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? <RefreshCw size={16} className="animate-spin" /> : <Award size={16} />}
              {submitting ? 'Issuing...' : 'Issue Certification'}
            </button>
          </div>
        </form>

        {/* Guidelines */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-1.5">
            <Shield size={15} className="text-blue-600" /> Certification Guidelines
          </h3>
          <ul className="space-y-1 text-xs text-blue-800">
            <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-600 mt-0.5 shrink-0" /> All compliance pages must be certified by the external auditor (100% compliance)</li>
            <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-600 mt-0.5 shrink-0" /> All evidence must be internally verified before certification</li>
            <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-600 mt-0.5 shrink-0" /> Framework version must be current and active</li>
            <li className="flex items-start gap-1.5"><CheckCircle size={12} className="text-blue-600 mt-0.5 shrink-0" /> Attach audit report URL for traceability</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IssueCertification;
